import React from 'react';
import { Helmet } from 'react-helmet';

import metaKeywordsContent from './MetaKeywords_Default';

const SEO = ({
  title,
  description,
  canonical,
  metaKeywords,
  ogImage, // Parameter opsional untuk URL gambar Open Graph spesifik halaman
  twitterImage, // Parameter opsional untuk URL gambar Twitter Card spesifik halaman
  type = 'website' // Parameter opsional untuk tipe Open Graph (e.g., 'article', 'profile', dll.)
}) => {

  // Nilai default untuk properti SEO jika tidak disediakan
  const defaultTitle = 'Rekanata Konstruksi - Kontraktor Profesional & Amanah di Bandung';
  const defaultDescription = 'Rekanata Konstruksi adalah jasa kontraktor bangunan profesional dan amanah di Bandung, melayani pembangunan rumah, kantor, masjid, ruko, interior, dan lanskap. Dapatkan kualitas terjamin dan layanan terbaik di seluruh Indonesia.';
  const defaultCanonical = 'https://rekanata.com/';
  // Asumsi gambar ini ada di folder `public/` proyek Anda
  const defaultOgImage = 'https://rekanata.com/rekanata-transparent-white-1200x630px.png'; // Contoh: URL gambar default untuk Open Graph
  const defaultTwitterImage = 'https://rekanata.com/twitter-image.jpg'; // Contoh: URL gambar default untuk Twitter Card

  // Tentukan nilai akhir yang akan digunakan, dengan prioritas parameter yang diteruskan
  const finalTitle = title || defaultTitle;
  const finalDescription = description || defaultDescription;
  const finalCanonical = canonical || defaultCanonical;
  const finalMetaKeywords = metaKeywords || metaKeywordsContent;
  const finalOgImage = ogImage || defaultOgImage;
  const finalTwitterImage = twitterImage || defaultTwitterImage;

  // Schema Markup (JSON-LD) - Memberikan data terstruktur ke mesin pencari.
  // Ini sangat penting untuk Google AI Summary, Featured Snippets, dan Knowledge Panels.
  // PASTIKAN ANDA MENGGANTI SEMUA PLACEHOLDER DENGAN INFORMASI ASLI DAN AKURAT UNTUK REKANATA KONSTRUKSI.
  const schemaOrgJSONLD = [
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Rekanata Konstruksi",
      "url": finalCanonical,
      "logo": "https://rekanata.com/rekanata-transparent-white.png",
      "image": "https://rekanata.com/rekanata-transparent-white-1200x630px.png",
      "description": finalDescription,
      "telephone": "+6282110002640",
      "email": "info@rekanata.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Jl. Cimincrang, Kavling Pos Giro no 10, Cimenerang, Kec. Gedebage",
        "addressLocality": "Bandung",
        "addressRegion": "Jawa Barat",
        "postalCode": "40294",
        "addressCountry": "ID"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -6.9489254,
        "longitude": 107.7066551
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": [
            "Monday",   // Hari pertama dalam minggu (Monday)
            "Tuesday",  // Hari kedua dalam minggu (Tuesday)
            "Wednesday",   // Hari ketiga dalam minggu (Wednesday)
            "Thursday",   // Hari keempat dalam minggu (Thursday)
            "Friday"    // Hari kelima dalam minggu (Friday)
          ],
          "opens": "08:00",   // Jam buka pada hari-hari tersebut (08:00 pagi)
          "closes": "17:00"   // Jam tutup pada hari-hari tersebut (05:00 sore)
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": "Saturday",  // Hari keenam dalam minggu (Saturday)
          "opens": "08:00",      // Jam buka pada hari Sabtu (08:00 pagi)
          "closes": "14:00"      // Jam tutup pada hari Sabtu (02:00 siang)
        }
      ],
      "areaServed": {
        "@type": "Place",
        "name": "Indonesia"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+6282110002640",
        "contactType": "Customer Service",
        "areaServed": "ID",
        "availableLanguage": ["Indonesian"]
      },
      "sameAs": [
        "https://www.facebook.com/rekanatacontractors/",
        "https://www.instagram.com/rekanata_konstruksi/",
        "https://www.tiktok.com/@rekanatacontractors/",
        "https://www.linkedin.com/in/rekanatacontractors/",
        "https://www.youtube.com/@rekanata.contractors/",
        "https://x.com/rekanata13"
      ]
    },
  ];

  return (
    <Helmet>
      {/* Title & Canonical Tag - Sangat penting untuk SEO dasar */}
      <title>{finalTitle}</title>
      <link rel="canonical" href={finalCanonical} />

      {/* Meta Description - Cuplikan yang muncul di hasil pencarian dan sumber data AI Summary */}
      <meta name="description" content={finalDescription} />

      {/* Meta Keywords - Tidak digunakan oleh Google, tetapi dipertahankan sesuai permintaan */}
      <meta name="keywords" content={finalMetaKeywords} />

      {/* Robots Meta Tag - Memberi tahu crawler cara mengindeks halaman dan mengikuti tautan */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" /> {/* Spesifik untuk Googlebot */}

      {/* Language and locale for SEO & social */}
      <meta name="language" content="id" />
      <meta property="og:locale" content="id_ID" />

      {/* Favicon - Berbagai ukuran dan jenis untuk kompatibilitas luas di berbagai perangkat dan browser */}
      {/* PASTIKAN FILE-FILE INI ADA DI FOLDER `public/` PROYEK ANDA */}
      <link rel="icon" type="image/x-icon" href="/favicon.ico" /> {/* Default .ico */}
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16px.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32px.png" />
      <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#5bbad5" /> {/* Untuk Safari Pinned Tabs */}
      <meta name="msapplication-TileColor" content="#da532c" /> {/* Warna tile untuk Windows */}
      <meta name="theme-color" content="#ffffff" /> {/* Warna tema untuk browser seluler (Chrome di Android) */}

      {/* Social: Open Graph Meta Tags (untuk Facebook, LinkedIn, WhatsApp, dll.) */}
      {/* Ini mengontrol bagaimana URL Anda tampil saat dibagikan di media sosial */}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={finalCanonical} />
      <meta property="og:image" content={finalOgImage} /> {/* URL gambar untuk dibagikan (disarankan 1200x630px) */}
      <meta property="og:image:width" content="1200" /> {/* Direkomendasikan lebar */}
      <meta property="og:image:height" content="630" /> {/* Direkomendasikan tinggi */}
      <meta property="og:image:alt" content={finalTitle} /> {/* Teks alternatif untuk gambar */}
      <meta property="og:site_name" content="Rekanata Konstruksi" /> {/* Nama situs yang muncul */}

      {/* Twitter Card Meta Tags (untuk Twitter/X) */}
      {/* Ini mengontrol bagaimana URL Anda tampil saat dibagikan di Twitter/X */}
      <meta name="twitter:card" content="summary_large_image" /> {/* Gunakan 'summary_large_image' untuk gambar besar */}
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalTwitterImage} /> {/* URL gambar untuk Twitter (disarankan 1200x675px) */}
      <meta name="twitter:image:alt" content={finalTitle} />
      <meta name="twitter:site" content="@rekanata13" /> {/* Ganti dengan handle Twitter/X ASLI Anda (misal: @YourCompanyHandle) */}

      {/* Geo Meta Tags - Penting untuk SEO Lokal di Bandung */}
      {/* Membantu mesin pencari memahami lokasi geografis bisnis Anda */}
      <meta name="geo.region" content="ID-JB" /> {/* Kode wilayah ISO 3166-2 untuk Jawa Barat */}
      <meta name="geo.placename" content="Bandung" /> {/* Nama kota/tempat */}
      <meta name="geo.position" content="-6.9487184;107.7060606" /> {/* Lintang dan Bujur */}
      {/* <meta name="ICBM" content="-6.94934,107.7068" /> Custom Lintang dan Bujur alternatif */}

      {/* JSON-LD Schema Markup - Script yang memasukkan data terstruktur ke dalam HTML */}
      <script type="application/ld+json">
        {JSON.stringify(schemaOrgJSONLD)}
      </script>
    </Helmet>
  );
};

export default SEO;